import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { FS } from "src/configs/constance";
import vaultsConfig from "src/configs/expedition-vaults";
import NFTCollectionContract from "src/contract-logic/logic/NFTCollectionContract";
import NFTExpeditionContract from "src/contract-logic/logic/NFTExpeditionContract";
import { resetAccountState } from "src/redux/account-slice";
import multiCall from "src/utils/multicall";
import CollectionQuery from "src/utils/query/onchain/multi-call-query/collection-query";

export const fetchCollectionsExpedition = createAsyncThunk(
  "expeditionSlice/fetchCollectionsExpedition",
  async ({ vaultId }, thunkAPI) => {
    const vault = vaultsConfig[vaultId];
    const accountAddress = thunkAPI.getState().accountSlice.address;
    const contract = NFTExpeditionContract.getContractExpedition(vault.contractAddress, {
      decimals: 18,
    });
    const collectionLength = await contract.getTokenOfOwnerBalance(accountAddress);
    const [collectionIds] = await Promise.all([
      multiCall(
        NFTExpeditionContract.ABI,
        new Array(collectionLength).fill(1).map((_, idx) => ({
          address: vault.contractAddress,
          name: "getTokenOfOwnerAtIndex",
          params: [accountAddress, idx],
        }))
      ),
    ]);
    const collectionIdsFlattened = collectionIds.flat();
    const { normalCollections } = await CollectionQuery.fetchBasicCollections(collectionIdsFlattened);

    let armorTokenIdArray = [];
    let helmetTokenIdArray = [];
    let shieldTokenIdArray = [];
    let weaponTokenIdArray = [];
    normalCollections.forEach((item, _) => {
      armorTokenIdArray.push(item.armorTokenId);
      helmetTokenIdArray.push(item.helmetTokenId);
      shieldTokenIdArray.push(item.shieldTokenId);
      weaponTokenIdArray.push(item.weaponTokenId);
    });

    const normalItemsCollections = await CollectionQuery.fetchNormalItems(
      armorTokenIdArray,
      helmetTokenIdArray,
      shieldTokenIdArray,
      weaponTokenIdArray
    );

    const v1 = [];
    const v2 = [];
    let counter = 0;
    for (const rawCollection of normalCollections) {
      if (rawCollection.setId == 1) v1.push({ ...rawCollection, ...normalItemsCollections[counter] });
      else if (rawCollection.setId == 2) v2.push({ ...rawCollection, ...normalItemsCollections[counter] });
      counter++;
    }
    return v1;
  }
);

export const fetchAllVaultExpeditionData = createAsyncThunk(
  "expeditionSlice/fetchAllVaultExpeditionData",
  async (_, thunkAPI) => {
    await Promise.all(
      Object.entries(vaultsConfig).map(([vaultId, _]) => thunkAPI.dispatch(fetchVaultExpeditionData({ vaultId })))
    );
  }
);

export const fetchVaultExpeditionData = createAsyncThunk(
  "expeditionSlice/fetchVaultExpeditionData",
  async ({ vaultId }, thunkAPI) => {
    const accountAddress = thunkAPI.getState().accountSlice.address;
    const rarities = [1, 2, 3, 4, 5, 6];
    const vault = vaultsConfig[vaultId];
    const contract = NFTExpeditionContract.getContractExpedition(vault.contractAddress, {
      decimals: 18,
    });
    const expeditionCount = await Promise.all(rarities.map(async (item) => await contract.getExpeditionCount(item)));
    const deployedCollections = await thunkAPI.dispatch(fetchCollectionsExpedition({ vaultId }));
    const totalKnightsOnDuty = await thunkAPI.dispatch(fetchTotalKnightsOnDuty({ vaultId }));
    const [successReward, hugeSuccessReward, time, price, maxTicketSpend] = await Promise.all([
      contract.getSuccessPayout(),
      contract.getHugeSuccessPayout(),
      contract.getExpeditionDuration(),
      contract.getExpeditionPrice(),
      contract.getMaxTicketSpend(),
    ]);
    if (accountAddress) {
      return {
        ...vault,
        successReward: successReward,
        hugeSuccessReward: hugeSuccessReward,
        time: time,
        price: price,
        expeditionCount: expeditionCount,
        totalKnightsOnDuty: totalKnightsOnDuty.payload,
        deployedCollections: deployedCollections.payload,
        maxTicketSpend,
      };
    } else {
      return {
        ...vault,
        successReward: successReward,
        hugeSuccessReward: hugeSuccessReward,
        time: time,
        price: price,
        expeditionCount: expeditionCount,
        totalKnightsOnDuty: totalKnightsOnDuty.payload,
        maxTicketSpend,
      };
    }
  }
);

export const fetchBuffSuccessRate = createAsyncThunk(
  "expeditionSlice/fetchBuffSuccessRate",
  async ({ vaultId, amount }) => {
    const vault = vaultsConfig[vaultId];
    const contract = NFTExpeditionContract.getContractExpedition(vault.contractAddress, {
      decimals: 18,
    });
    const buffSuccessRate = await contract.getBuffWinRate(amount);
    return {
      vaultId,
      buffSuccessRate,
    };
  }
);

export const fetchExpeditionCount = createAsyncThunk("expeditionSlice/fetchExpeditionCount", async ({ vaultId }) => {
  const vault = vaultsConfig[vaultId];
  const rarities = [1, 2, 3, 4, 5, 6];
  const contract = NFTExpeditionContract.getContractExpedition(vault.contractAddress, {
    decimals: 18,
  });
  const res = await Promise.all(rarities.map(async (item) => await contract.getExpeditionCount(item)));
  return res;
});

export const fetchTotalKnightsOnDuty = createAsyncThunk(
  "expeditionSlice/fetchTotalKnightsOnDuty",
  async ({ vaultId }) => {
    const vault = vaultsConfig[vaultId];
    const res = await NFTCollectionContract.getBalance(vault.contractAddress);
    return res;
  }
);

const initialState = {
  vaults: {},
  fetchingStatus: FS.INITIAL,
};

const expeditionSlice = createSlice({
  name: "expeditionSlice",
  initialState: initialState,
  reducer: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchAllVaultExpeditionData.pending, (state, _) => {
        state.fetchingStatus = FS.FETCHING;
      })
      .addCase(fetchAllVaultExpeditionData.rejected, (state, action) => {
        state.fetchingStatus = FS.FAIL;
        console.error(action);
      })
      .addCase(fetchAllVaultExpeditionData.fulfilled, (state, _) => {
        state.fetchingStatus = FS.SUCCESS;
      })
      .addCase(fetchVaultExpeditionData.fulfilled, (state, action) => {
        const vaultId = action.meta.arg.vaultId;
        state.vaults[vaultId] = { ...state.vaults[vaultId], ...action.payload };
      })
      .addCase(resetAccountState, (state, _) => {
        Object.entries(state.vaults).forEach(([_, item]) => (item.deployedCollections = undefined));
      })
      .addCase(fetchBuffSuccessRate.fulfilled, (state, action) => {
        const { vaultId, buffSuccessRate } = action.payload;
        state.vaults[vaultId].buffSuccessRate = buffSuccessRate;
      })
      .addCase(fetchBuffSuccessRate.rejected, (state, action) => {
        const { vaultId } = action.meta.arg;
        delete state.vaults[vaultId].buffSuccessRate;
      }),
});

export default expeditionSlice.reducer;
